import { Box, Stack, Typography } from "@mui/material";
import { Flex } from "@zfp-akademie-a-s/zfp-design";
import { CheckIcon } from "@zfp-akademie-a-s/zfp-design/icons";
import type { GoalOverview } from "Infrastructure/ApiZfp/Schema";
import { Radius, Spacing } from "Infrastructure/Themes/Themes";
import { useResource } from "Infrastructure/Translations/Resources";
import { inflectYears } from "Infrastructure/Utils/LanguageUtils";
import { useClientBirthDate } from "../Hooks/useClientBirthDate";
import { GoalCard } from "./GoalCard";
import { ICON_TILE_SIZE } from "./GoalCardHeader";
import { TimelineAxisCell, TimelineContentCell, TimelineDateCell, TimelineRow, axisMarkerRing } from "./TimelineRow";
import { formatMonthYear, getClientAge } from "./utils/dates";
import { type GoalTone, getGoalTone } from "./utils/goalTone";

const GoalMarker = ({ tone }: { tone: GoalTone }) =>
  tone === "completed" ? (
    <Flex
      alignItems="center"
      justifyContent="center"
      width={20}
      height={20}
      borderRadius={Radius.MAX}
      bgcolor="success.light"
      color="success.dark"
      sx={axisMarkerRing}
    >
      <CheckIcon sx={{ fontSize: 12 }} />
    </Flex>
  ) : (
    <Box
      width={12}
      height={12}
      borderRadius={Radius.MAX}
      bgcolor={tone === "archived" ? "text.secondary" : "primary.main"}
      sx={axisMarkerRing}
    />
  );

type Props = {
  goal: GoalOverview;
  onEdit?: (goal: GoalOverview) => void;
};

export const GoalTimelineRow = ({ goal, onEdit }: Props) => {
  const { t } = useResource();
  const birthDate = useClientBirthDate();
  const { targetDate } = goal;

  return (
    <TimelineRow py={Spacing.M}>
      {/* The date and the marker are pushed down by the card's padding, so they line up with its icon tile. */}
      <TimelineDateCell pt={Spacing.XL}>
        {targetDate && (
          <Stack gap={Spacing.XS}>
            <Typography variant="caption" color="text.secondary">
              {formatMonthYear(targetDate)}
            </Typography>
            {birthDate && (
              <Typography variant="caption" color="text.secondary">
                {inflectYears(getClientAge(birthDate, targetDate), t)}
              </Typography>
            )}
          </Stack>
        )}
      </TimelineDateCell>

      <TimelineAxisCell>
        <Flex alignItems="center" height={ICON_TILE_SIZE} mt={Spacing.XL}>
          <GoalMarker tone={getGoalTone(goal)} />
        </Flex>
      </TimelineAxisCell>

      <TimelineContentCell>
        <GoalCard goal={goal} onClick={onEdit && (() => onEdit(goal))} />
      </TimelineContentCell>
    </TimelineRow>
  );
};
